/**
 * @fileoverview Modal dialog for loading PDF files from URL with history support.
 * @module ui/PdfUrlDialog
 */

import { BaseDialog } from './BaseDialog.js';
import { escapeHtml } from '../utils/jrfuncs.js';


/**
 * Modal dialog for entering or selecting a PDF URL to load.
 * Provides a dropdown of previously entered URLs for quick access.
 * @class
 * @extends BaseDialog
 */
export class PdfUrlDialog extends BaseDialog {
    /**
     * Creates a new PdfUrlDialog instance.
     * @param {Object} options - Configuration options
     * @param {string[]} [options.urlHistory=[]] - Array of previously entered URLs
     * @param {string} [options.lastUrl=''] - The last loaded URL (default value)
     */
    constructor(options = {}) {
        super({ idPrefix: 'pdfUrlDialog' });

        this.urlHistory = options.urlHistory || [];
        this.lastUrl = options.lastUrl || '';
    }


    /**
     * Returns the HTML for the dialog.
     * @returns {string} Modal HTML string
     * @override
     */
    getModalHtml() {
        // Build history options for the datalist
        const historyOptions = this.urlHistory
            .map(url => `<option value="${escapeHtml(url)}">`)
            .join('');

        const bodyHtml = `
            <div class="pdf-url-dialog-content">
                <div class="mb-3">
                    <label for="${this.modalId}_urlInput" class="form-label">
                        Either load a PDF from a website url (see <a href="https://nynoir.org/cases" target="_blank">nynoir.org</a> case list):
                    </label>
                    <input
                        type="text"
                        class="form-control"
                        id="${this.modalId}_urlInput"
                        list="${this.modalId}_urlHistory"
                        placeholder="Enter PDF URL or select from history..."
                        autocomplete="off"
                    >
                    <datalist id="${this.modalId}_urlHistory">
                        ${historyOptions}
                    </datalist>
                </div>
                ${this.urlHistory.length > 0 ? `
                <div class="mb-3">
                    <label class="form-label text-muted small">... Or choose a recent URL:</label>
                    <select class="form-select form-select-sm" id="${this.modalId}_historySelect" size="4">
                        ${this.urlHistory.map(url =>
                            `<option value="${escapeHtml(url)}">${escapeHtml(this.truncateUrl(url))}</option>`
                        ).join('')}
                    </select>
                </div>
                ` : ''}
                <div class="mb-3">
                    <hr class="my-2">
                    <label class="form-label text-muted small"><strong>... Or load a local pdf from your computer:</strong></label>
                    <input
                        type="file"
                        class="d-none"
                        id="${this.modalId}_fileInput"
                        accept=".pdf,application/pdf"
                    >
                    <div class="d-flex align-items-center gap-2">
                        <button type="button" class="btn btn-outline-secondary" id="${this.modalId}_fileBtn">
                            <i class="bi bi-folder2-open"></i> Choose Local File...
                        </button>
                        <span class="text-muted small" id="${this.modalId}_fileName"></span>
                    </div>
                </div>
            </div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-outline-danger" id="${this.modalId}_unloadBtn">
                <i class="bi bi-x-circle"></i> Unload current PDF
            </button>
            <span class="flex-grow-1"></span>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Cancel
            </button>
            <button type="button" class="btn btn-primary" id="${this.modalId}_loadBtn">
                <i class="bi bi-box-arrow-in-down"></i> Load
            </button>
        `;

        return this.buildModalHtml({
            title: 'Load Case PDF',
            icon: 'bi-file-earmark-pdf',
            bodyHtml,
            footerHtml,
            size: 'modal-md'
        });
    }


    /**
     * Called before the modal is shown. Sets up initial values.
     * @override
     */
    onBeforeShow() {
        const urlInput = this.getElement('_urlInput');
        if (urlInput && this.lastUrl) {
            urlInput.value = this.lastUrl;
        }
    }


    /**
     * Called after the modal is shown. Focuses the input.
     * @override
     */
    onAfterShow() {
        const urlInput = this.getElement('_urlInput');
        if (urlInput) {
            urlInput.focus();
            urlInput.select();
        }
    }


    /**
     * Sets up event listeners for modal interactions.
     * @override
     */
    setupEventListeners() {
        const urlInput = this.getElement('_urlInput');
        const loadBtn = this.getElement('_loadBtn');
        const unloadBtn = this.getElement('_unloadBtn');
        const historySelect = this.getElement('_historySelect');
        const fileInput = this.getElement('_fileInput');
        const fileBtn = this.getElement('_fileBtn');
        const fileNameEl = this.getElement('_fileName');

        // Track selected file data
        this.selectedFileData = null;
        this.selectedFileName = null;

        // Load button click
        loadBtn.addEventListener('click', () => {
            // Prioritize file data if a file was selected
            if (this.selectedFileData) {
                this.resolve({
                    action: 'loadFile',
                    data: this.selectedFileData,
                    fileName: this.selectedFileName
                });
                return;
            }
            const url = urlInput.value.trim();
            if (url) {
                this.resolve({ action: 'load', url });
            }
        });

        // Unload button click
        unloadBtn.addEventListener('click', () => {
            this.resolve({ action: 'unload', url: null });
        });

        // Enter key in input
        urlInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const url = urlInput.value.trim();
                if (url) {
                    // Clear file selection when loading URL
                    this.selectedFileData = null;
                    this.selectedFileName = null;
                    this.resolve({ action: 'load', url });
                }
            }
        });

        // Clear file selection when URL input changes
        urlInput.addEventListener('input', () => {
            if (urlInput.value.trim()) {
                this.selectedFileData = null;
                this.selectedFileName = null;
                fileNameEl.textContent = '';
                fileInput.value = '';
            }
        });

        // History select - populate input on selection
        if (historySelect) {
            historySelect.addEventListener('change', () => {
                const selectedUrl = historySelect.value;
                if (selectedUrl) {
                    urlInput.value = selectedUrl;
                    urlInput.focus();
                    // Clear file selection
                    this.selectedFileData = null;
                    this.selectedFileName = null;
                    fileNameEl.textContent = '';
                    fileInput.value = '';
                }
            });

            // Double-click to load immediately
            historySelect.addEventListener('dblclick', () => {
                const selectedUrl = historySelect.value;
                if (selectedUrl) {
                    this.resolve({ action: 'load', url: selectedUrl });
                }
            });
        }

        // File button click - trigger hidden file input
        fileBtn.addEventListener('click', () => {
            fileInput.click();
        });

        // File input change - read the selected file
        fileInput.addEventListener('change', async () => {
            const file = fileInput.files[0];
            if (!file) return;

            try {
                fileNameEl.textContent = 'Reading...';
                const arrayBuffer = await file.arrayBuffer();
                this.selectedFileData = arrayBuffer;
                this.selectedFileName = file.name;
                fileNameEl.textContent = file.name;
                // Clear URL input to indicate file takes precedence
                urlInput.value = '';
            } catch (error) {
                console.error('Failed to read file:', error);
                fileNameEl.textContent = 'Error reading file';
                this.selectedFileData = null;
                this.selectedFileName = null;
            }
        });
    }


    /**
     * Returns the default result when cancelled.
     * @returns {Object} Result with action: 'cancel'
     * @override
     */
    getDefaultResult() {
        return { action: 'cancel', url: null };
    }


    /**
     * Truncates a URL for display, keeping the filename visible.
     * @param {string} url - URL to truncate
     * @param {number} [maxLength=60] - Maximum display length
     * @returns {string} Truncated URL
     * @private
     */
    truncateUrl(url, maxLength = 60) {
        if (url.length <= maxLength) {
            return url;
        }

        // Try to keep the filename visible
        const lastSlash = url.lastIndexOf('/');
        if (lastSlash > 0) {
            const filename = url.substring(lastSlash + 1);
            const protocol = url.substring(0, url.indexOf('://') + 3);
            const remaining = maxLength - protocol.length - filename.length - 3; // 3 for "..."

            if (remaining > 10) {
                const host = url.substring(protocol.length, lastSlash);
                return protocol + host.substring(0, remaining) + '.../' + filename;
            }
        }

        return url.substring(0, maxLength - 3) + '...';
    }


    /**
     * Shows the PDF URL dialog (singleton - only one can be open at a time).
     * @param {Object} options - Configuration options
     * @param {string[]} [options.urlHistory=[]] - Array of previously entered URLs
     * @param {string} [options.lastUrl=''] - The last loaded URL (default value)
     * @returns {Promise<{action: string, url: string|null}>} Result with action ('load' or 'cancel') and url
     */
    static async showPdfUrlDialog(options = {}) {
        return BaseDialog.showSingleton(PdfUrlDialog, options);
    }
}
